import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { Category, MenuItem, OrderItem, Order, Payment, BusinessInfo, PublicOrderingDay } from '../types';
import {
  isMinutesInPeriods,
  listPickupHHmm,
  minutesInTimeZone,
  parseTimeToMinutes,
  resolveDayPeriods,
  weekdayInTimeZone,
  zonedTodayAtMinutes,
} from '../lib/businessHours';
import { useMenuItems, useCategories, useOrders } from '../hooks';
import { ordersService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { emailService } from '../lib/email';
import ShoppingCartIcon from '../components/icons/ShoppingCartIcon';
import GiftIcon from '../components/icons/GiftIcon';
import XCircleIcon from '../components/icons/XCircleIcon';
import CheckCircleIcon from '../components/icons/CheckCircleIcon';
import CreditCardIcon from '../components/icons/CreditCardIcon';
import ArrowLeftIcon from '../components/icons/ArrowLeftIcon';
import StarSolidIcon from '../components/icons/StarSolidIcon';
import MenuItemImage from '../components/menu/MenuItemImage';
import MeatOptionModal from '../components/pos/MeatOptionModal';
import { totalsFromGstInclusiveLineItems } from '../lib/orderUtils';
import {
  filterCategoriesForChannel,
  filterMenuItemsForChannel,
} from '../lib/menuChannelAvailability';
import CustomerAppAuthPanel from '../components/online/CustomerAppAuthPanel';
import OnlineMealPlanPanel from '../components/online/OnlineMealPlanPanel';
import {
  isCustomerAppAuthConfigured,
  signOutCustomerApp,
  subscribeCustomerAppAuth,
} from '../lib/customerAppAuth';
import type { User as FirebaseUser } from 'firebase/auth';

/** Survives Windcave HPP redirect: callback URLs use ?wc_return=… and do not include session id. */
const WC_PENDING_SESSION_KEY = 'onlineOrderWindcaveSessionV1';
const WC_PENDING_ORDER_JSON_KEY = 'onlineOrderWindcavePlacedOrderJsonV1';
const WC_PENDING_ORDER_ID_KEY = 'onlineOrderWindcaveOrderIdV1';
const WC_PENDING_EMAIL_KEY = 'onlineOrderWindcaveEmailV1';
/** Survives URL cleanup so confirmation still shows if the page remounts. */
const ONLINE_CONFIRMATION_STATE_KEY = 'onlineOrderConfirmationStateV1';
const ONLINE_ORDER_ALLERGY_ACCEPTED_KEY = 'onlineOrderAllergyAcceptedV1';
/** Cart hold: pickup slot reserved for 10 minutes while customer shops. */
const CART_HOLD_MS = 10 * 60 * 1000;
const CART_EXPIRES_AT_KEY = 'onlineOrderCartExpiresAtV1';

/** Windcave HPP session create — card only for online ordering. */
const WINDCAVE_HPP_SESSION_METHODS: string[] = ['card'];

/** Format seconds remaining for cart hold UI. */
function formatCartHoldLabel(secondsLeft: number): string {
  const m = Math.floor(secondsLeft / 60);
  const s = String(secondsLeft % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function persistWindcavePendingCheckout(sessionId: string, orderDoc: Order): void {
  if (typeof window === 'undefined') return;
  const orderJson = JSON.stringify(orderDoc);
  const email = (orderDoc.onlineCustomerEmail || '').trim();
  try {
    window.sessionStorage.setItem(WC_PENDING_SESSION_KEY, sessionId);
    window.sessionStorage.setItem(WC_PENDING_ORDER_JSON_KEY, orderJson);
    window.localStorage.setItem(WC_PENDING_SESSION_KEY, sessionId);
    window.localStorage.setItem(WC_PENDING_ORDER_JSON_KEY, orderJson);
    if (orderDoc.id) {
      window.localStorage.setItem(WC_PENDING_ORDER_ID_KEY, String(orderDoc.id));
    }
    if (email) {
      window.localStorage.setItem(WC_PENDING_EMAIL_KEY, email);
    }
  } catch {
    // quota / private mode
  }
}

function readWindcavePendingOrderJson(): Order | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw =
      window.sessionStorage.getItem(WC_PENDING_ORDER_JSON_KEY) ||
      window.localStorage.getItem(WC_PENDING_ORDER_JSON_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Order;
  } catch {
    return null;
  }
}

function readWindcavePendingEmail(): string {
  if (typeof window === 'undefined') return '';
  try {
    const fromOrder = readWindcavePendingOrderJson()?.onlineCustomerEmail;
    if (fromOrder?.trim()) return fromOrder.trim();
    return window.localStorage.getItem(WC_PENDING_EMAIL_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

function clearWindcavePendingCheckout(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(WC_PENDING_SESSION_KEY);
    window.sessionStorage.removeItem(WC_PENDING_ORDER_JSON_KEY);
    window.localStorage.removeItem(WC_PENDING_SESSION_KEY);
    window.localStorage.removeItem(WC_PENDING_ORDER_JSON_KEY);
    window.localStorage.removeItem(WC_PENDING_ORDER_ID_KEY);
    window.localStorage.removeItem(WC_PENDING_EMAIL_KEY);
  } catch {
    // ignore
  }
}

type OnlineConfirmationState = {
  orderId: string;
  placedOrder: Order;
  windcaveResult: {
    authorised: boolean;
    responseText?: string;
    settledPaymentMethod?: 'card_online' | 'account2account';
  };
};

function readOnlineConfirmationState(): OnlineConfirmationState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(ONLINE_CONFIRMATION_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OnlineConfirmationState;
  } catch {
    return null;
  }
}

function persistOnlineConfirmationState(state: OnlineConfirmationState): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(ONLINE_CONFIRMATION_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function clearOnlineConfirmationState(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(ONLINE_CONFIRMATION_STATE_KEY);
  } catch {
    // ignore
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

interface OnlineOrderPageProps {
  onOrderPlaced?: (order: Order) => void;
  businessInfo?: BusinessInfo;
  /** Week schedule from public_config. When set, this replaces the single start/end window. */
  orderingSchedule?: { timezone?: string; days: PublicOrderingDay[] };
}

/** Check if current local time is within [start, end). Overnight (end < start) means e.g. 22:00–02:00. */
function isWithinOrderingHours(startTime: string | undefined, endTime: string | undefined): boolean {
  if (!startTime?.trim() || !endTime?.trim()) return true; // No restriction = 24/7
  const start = parseTimeToMinutes(startTime);
  const end = parseTimeToMinutes(endTime);
  if (start == null || end == null) return true;
  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();
  if (start <= end) return current >= start && current < end;
  return current >= start || current < end; // Overnight window
}

/**
 * Last moment (local) for which we offer 15‑minute pickup slots: same calendar day as `onlineOrderEndTime`,
 * or next calendar day when the online window crosses midnight. If `onlineOrderEndTime` is unset, end of day 23:45.
 */
function getPickupSlotsEndDate(now: Date, onlineOrderStart: string | undefined, onlineOrderEnd: string | undefined): Date {
  const endRaw = onlineOrderEnd?.trim();
  if (!endRaw) {
    const d = new Date(now);
    d.setHours(23, 45, 0, 0);
    return d;
  }
  const closeMin = parseTimeToMinutes(endRaw);
  if (closeMin == null) {
    const d = new Date(now);
    d.setHours(23, 45, 0, 0);
    return d;
  }
  const openRaw = onlineOrderStart?.trim();
  const openMin = openRaw ? parseTimeToMinutes(openRaw) : null;
  const nm = now.getHours() * 60 + now.getMinutes();
  const overnight = openMin != null && openMin > closeMin;

  const atLocalMinutes = (base: Date, dayOffset: number, mins: number) => {
    const x = new Date(base);
    x.setDate(x.getDate() + dayOffset);
    x.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
    return x;
  };

  if (!overnight || openMin == null) {
    return atLocalMinutes(now, 0, closeMin);
  }
  if (nm >= openMin) {
    return atLocalMinutes(now, 1, closeMin);
  }
  return atLocalMinutes(now, 0, closeMin);
}

/** Format "HH:mm" to friendly string e.g. "9:00 AM". */
function formatOrderingTime(t: string): string {
  const [h, m] = t.trim().split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return t;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
}

/** Pickup slot for confirmation: prefer Firestore `pickupTime` ms; else today + checkout `HH:mm` if still set. */
function formatPickupDisplay(pickupTimestampMs: number | undefined, fallbackHHmm: string): string | null {
  if (typeof pickupTimestampMs === 'number' && Number.isFinite(pickupTimestampMs)) {
    return new Date(pickupTimestampMs).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  const raw = fallbackHHmm.trim();
  if (!/^\d{1,2}:\d{2}$/.test(raw)) return null;
  const [h, m] = raw.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const OnlineOrderPage: React.FC<OnlineOrderPageProps> = ({ onOrderPlaced, businessInfo, orderingSchedule }) => {
  const isFirebaseBackend = getDataSource() === 'firebase';
  const { menuItems, loading: menuLoading } = useMenuItems();
  const { categories } = useCategories();
  const { orders, create: createOrder } = useOrders(!isFirebaseBackend);

  const orderingStart = businessInfo?.onlineOrderStartTime?.trim();
  const orderingEnd = businessInfo?.onlineOrderEndTime?.trim();
  const usingSchedule = Boolean(orderingSchedule?.days?.length);
  const scheduleTimezone = orderingSchedule?.timezone?.trim() || 'Pacific/Auckland';
  const todayPeriods = usingSchedule
    ? resolveDayPeriods(
        orderingSchedule!.days.find((day) => day.day === weekdayInTimeZone(new Date(), scheduleTimezone)) || {
          closed: true,
        }
      )
    : [];
  const orderingHoursRestricted = usingSchedule || Boolean(orderingStart && orderingEnd);
  const withinOrderingHours = usingSchedule
    ? isMinutesInPeriods(minutesInTimeZone(new Date(), scheduleTimezone), todayPeriods, false)
    : !orderingHoursRestricted || isWithinOrderingHours(orderingStart, orderingEnd);
  const closedHoursText = usingSchedule
    ? todayPeriods.length > 0
      ? `Online ordering is available ${todayPeriods
          .map((period) => `${formatOrderingTime(period.open)}–${formatOrderingTime(period.close)}`)
          .join(', ')}.`
      : 'Online ordering is closed today.'
    : orderingStart && orderingEnd
      ? `Online ordering is available between ${formatOrderingTime(orderingStart)} and ${formatOrderingTime(orderingEnd)}.`
      : 'Online ordering is closed.';
  
  const [view, setView] = useState<
    'menu' | 'cart' | 'checkout' | 'payment' | 'confirmation' | 'payment_interrupted' | 'mealPlans'
  >(() => {
    if (typeof window === 'undefined') return 'menu';
    const params = new URLSearchParams(window.location.search);
    const mp = params.get('mp_return');
    if (mp === 'approved' || mp === 'declined' || mp === 'cancelled') return 'mealPlans';
    const saved = readOnlineConfirmationState();
    return saved ? 'confirmation' : 'menu';
  });
  const [mealPlanReturnMessage, setMealPlanReturnMessage] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const mp = new URLSearchParams(window.location.search).get('mp_return');
    if (mp === 'approved') return 'Payment received — your meal plan will activate shortly. Pull to refresh if needed.';
    if (mp === 'declined') return 'Payment was declined. You can try subscribing again.';
    if (mp === 'cancelled') return 'Payment was cancelled.';
    return null;
  });
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string | null>(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    postcode: '',
    specialInstructions: '', // Special instructions for the entire order (including allergies)
  });
  const [paymentMethod, setPaymentMethod] = useState<'card_online' | 'account2account' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  /** Shown during Firebase place order + Windcave session (perceived speed). */
  const [firebaseCheckoutPhase, setFirebaseCheckoutPhase] = useState<'saving_order' | 'opening_payment' | null>(null);
  const [orderId, setOrderId] = useState<string | null>(() => readOnlineConfirmationState()?.orderId ?? null);
  const [error, setError] = useState<string | null>(null);
  const [hasAcceptedAllergyWarning, setHasAcceptedAllergyWarning] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (window.localStorage.getItem(ONLINE_ORDER_ALLERGY_ACCEPTED_KEY) === '1') return true;
      // Windcave HPP return: sessionStorage may survive when localStorage does not on some mobile browsers.
      const params = new URLSearchParams(window.location.search);
      const wc = params.get('wc_return');
      if (wc === 'approved' || wc === 'declined' || wc === 'cancelled') {
        return window.sessionStorage.getItem(ONLINE_ORDER_ALLERGY_ACCEPTED_KEY) === '1';
      }
    } catch {
      // ignore
    }
    return false;
  });
  const [cartExpiresAt, setCartExpiresAt] = useState<number | null>(null);
  const [cartHoldSecondsLeft, setCartHoldSecondsLeft] = useState<number | null>(null);
  /** Full-page return from Windcave: avoid flashing the allergy gate before payment verification runs. */
  const [windcaveReturnPending, setWindcaveReturnPending] = useState(() => {
    if (typeof window === 'undefined' || getDataSource() !== 'firebase') return false;
    const params = new URLSearchParams(window.location.search);
    const wc = params.get('wc_return');
    if (wc === 'approved' || wc === 'declined' || wc === 'cancelled') return true;
    if (params.get('sessionId') || params.get('id') || params.get('session_id')) return true;
    return false;
  });
  /** Windcave returned but payment session could not be resumed (e.g. cleared storage / different device). */
  const [windcaveReturnBlocked, setWindcaveReturnBlocked] = useState(false);
  const [detailItem, setDetailItem] = useState<MenuItem | null>(null);
  const [meatOptionModalItem, setMeatOptionModalItem] = useState<MenuItem | null>(null);
  const categorySectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const categoryHeadingRefs = useRef<Map<string, HTMLHeadingElement>>(new Map());
  const categoryTabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const menuHeaderRef = useRef<HTMLElement | null>(null);
  const isManualCategoryScrollRef = useRef(false);
  const manualCategoryScrollTimerRef = useRef<number | null>(null);
  const [menuStickyOffset, setMenuStickyOffset] = useState(168);
  const [pickupTime, setPickupTime] = useState<string>('');
  const [customerAppUser, setCustomerAppUser] = useState<FirebaseUser | null>(null);
  const [customerAppAuthReady, setCustomerAppAuthReady] = useState(!isFirebaseBackend);
  const [placedOrder, setPlacedOrder] = useState<Order | null>(
    () => readOnlineConfirmationState()?.placedOrder ?? null
  );
  const [windcaveResult, setWindcaveResult] = useState<{
    authorised: boolean;
    responseText?: string;
    settledPaymentMethod?: 'card_online' | 'account2account';
  } | null>(() => readOnlineConfirmationState()?.windcaveResult ?? null);

  // Load cart and pickup time from local storage on first render (browser only)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const savedCart = window.localStorage.getItem('onlineOrderCartV1');
      let parsedCart: OrderItem[] = [];
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        if (Array.isArray(parsed)) {
          parsedCart = parsed as OrderItem[];
          setCart(parsedCart);
        }
      }
      const savedPickup = window.localStorage.getItem('onlineOrderPickupTimeV1');
      if (savedPickup) {
        setPickupTime(savedPickup);
      }
      if (parsedCart.length > 0) {
        const rawExpires = window.sessionStorage.getItem(CART_EXPIRES_AT_KEY);
        const expires = rawExpires ? Number(rawExpires) : NaN;
        if (Number.isFinite(expires) && expires > Date.now()) {
          setCartExpiresAt(expires);
        } else {
          window.localStorage.removeItem('onlineOrderCartV1');
          window.localStorage.removeItem('onlineOrderPickupTimeV1');
          window.sessionStorage.removeItem(CART_EXPIRES_AT_KEY);
          setCart([]);
          setPickupTime('');
        }
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get('mp_return')) return;
    params.delete('mp_return');
    params.delete('purchaseId');
    const next = params.toString();
    const url = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', url);
  }, []);

  useEffect(() => {
    if (!isFirebaseBackend) {
      setCustomerAppAuthReady(true);
      return;
    }
    if (!isCustomerAppAuthConfigured()) {
      setCustomerAppAuthReady(true);
      setCustomerAppUser(null);
      return;
    }
    const unsub = subscribeCustomerAppAuth((user) => {
      setCustomerAppUser(user);
      setCustomerAppAuthReady(true);
      if (user) {
        setCustomerInfo((prev) => ({
          ...prev,
          name: prev.name || user.displayName || '',
          email: user.email || prev.email,
        }));
      }
    });
    return unsub;
  }, [isFirebaseBackend]);

  const armCartHoldTimer = () => {
    const expires = Date.now() + CART_HOLD_MS;
    setCartExpiresAt(expires);
    try {
      window.sessionStorage.setItem(CART_EXPIRES_AT_KEY, String(expires));
    } catch {
      // ignore
    }
  };

  const clearCartHoldTimer = () => {
    setCartExpiresAt(null);
    setCartHoldSecondsLeft(null);
    try {
      window.sessionStorage.removeItem(CART_EXPIRES_AT_KEY);
    } catch {
      // ignore
    }
  };

  const expireCartHold = () => {
    clearCartHoldTimer();
    setCart([]);
    setPickupTime('');
    try {
      window.localStorage.removeItem('onlineOrderCartV1');
      window.localStorage.removeItem('onlineOrderPickupTimeV1');
    } catch {
      // ignore
    }
    setError('Your cart expired after 10 minutes. Please add your items again and choose a new pickup time.');
    setView('menu');
  };

  useEffect(() => {
    if (cart.length === 0) {
      clearCartHoldTimer();
      return;
    }
    if (cartExpiresAt == null) {
      armCartHoldTimer();
    }
  }, [cart.length, cartExpiresAt]);

  useEffect(() => {
    if (!cartExpiresAt || cart.length === 0) {
      setCartHoldSecondsLeft(null);
      return;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((cartExpiresAt - Date.now()) / 1000));
      setCartHoldSecondsLeft(left);
      if (left <= 0) {
        expireCartHold();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [cartExpiresAt, cart.length]);

  // Persist cart and pickup time whenever they change
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('onlineOrderCartV1', JSON.stringify(cart));
      window.localStorage.setItem('onlineOrderPickupTimeV1', pickupTime || '');
    } catch {
      // Ignore storage errors
    }
  }, [cart, pickupTime]);

  /** Older builds stored acceptance in localStorage (permanent). Clear so the gate can show again. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(ONLINE_ORDER_ALLERGY_ACCEPTED_KEY);
    } catch {
      // ignore
    }
  }, []);

  const resetCustomerForm = () => {
    setCustomerInfo({
      name: '',
      email: '',
      phone: '',
      streetAddress: '',
      city: '',
      postcode: '',
      specialInstructions: '',
    });
  };

  const resetAllergyGateForNewOrder = () => {
    setHasAcceptedAllergyWarning(false);
    try {
      window.sessionStorage.removeItem(ONLINE_ORDER_ALLERGY_ACCEPTED_KEY);
    } catch {
      // ignore
    }
  };

  /** After Windcave HPP redirect: resolve payment via Query Session (callable); do not trust URL alone. */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const wcReturn = params.get('wc_return');
    const isWcBrowserReturn =
      wcReturn === 'approved' || wcReturn === 'declined' || wcReturn === 'cancelled';

    if (!isFirebaseBackend) {
      if (isWcBrowserReturn) {
        setWindcaveReturnPending(false);
        setError('Online card payment is not available in demo mode. Use the live dev site.');
        setView('menu');
      }
      return;
    }

    if (!isWcBrowserReturn && !params.get('sessionId') && !params.get('id') && !params.get('session_id')) {
      return;
    }

    let sessionId =
      params.get('sessionId') ||
      params.get('id') ||
      params.get('session_id') ||
      null;
    if (!sessionId) {
      try {
        sessionId =
          window.sessionStorage.getItem(WC_PENDING_SESSION_KEY) ||
          window.localStorage.getItem(WC_PENDING_SESSION_KEY);
      } catch {
        sessionId = null;
      }
    }

    const orderIdFromUrl = params.get('orderId')?.trim() || '';
    const orderIdFromStorage =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(WC_PENDING_ORDER_ID_KEY)?.trim() || ''
        : '';
    const recoveryOrderId = orderIdFromUrl || orderIdFromStorage;
    const recoveryEmail = readWindcavePendingEmail();
    const canRecoverByOrder = Boolean(recoveryOrderId && recoveryEmail);

    if (!sessionId && !(isWcBrowserReturn && canRecoverByOrder)) {
      if (isWcBrowserReturn) {
        setWindcaveReturnPending(false);
        setWindcaveReturnBlocked(true);
        setError(
          'Could not resume your payment session. Use the same phone or browser you paid with, or contact us with your order number from your email.'
        );
      }
      return;
    }

    let cancelled = false;
    (async () => {
      setIsProcessing(true);
      setError(null);
      try {
        const { callWindcavePaymentStatus } = await import('../lib/onlineOrderingApi');
        const statusParams = {
          sessionId: sessionId || undefined,
          orderId: canRecoverByOrder ? recoveryOrderId : undefined,
          customerEmail: canRecoverByOrder ? recoveryEmail : undefined,
        };

        const maxAttempts = wcReturn === 'approved' ? 10 : 1;
        let status = await callWindcavePaymentStatus(statusParams);
        for (let attempt = 1; attempt < maxAttempts && !status.authorised && wcReturn === 'approved'; attempt++) {
          if (cancelled) return;
          await sleep(2000);
          status = await callWindcavePaymentStatus(statusParams);
        }

        if (cancelled) return;
        setWindcaveResult({
          authorised: status.authorised,
          responseText: status.responseText,
          settledPaymentMethod: status.settledPaymentMethod,
        });
        if (status.orderId) {
          setOrderId(status.orderId);
        }
        let orderForReceipt: Order | null = status.order ?? null;
        if (!orderForReceipt) {
          const restored = readWindcavePendingOrderJson();
          if (restored && (!status.orderId || restored.id === status.orderId)) {
            orderForReceipt = {
              ...restored,
              paymentStatus: status.authorised ? 'paid' : 'unpaid',
            };
          }
        }
        if (orderForReceipt) {
          setPlacedOrder(orderForReceipt);
        }
        setPaymentMethod(null);
        if (status.authorised) {
          const confirmedOrderId = status.orderId || orderForReceipt?.id || recoveryOrderId || null;
          if (confirmedOrderId) setOrderId(confirmedOrderId);
          const windcaveSnapshot = {
            authorised: true as const,
            responseText: status.responseText,
            settledPaymentMethod: status.settledPaymentMethod,
          };
          setWindcaveResult(windcaveSnapshot);
          if (orderForReceipt && confirmedOrderId) {
            persistOnlineConfirmationState({
              orderId: confirmedOrderId,
              placedOrder: { ...orderForReceipt, paymentStatus: 'paid' },
              windcaveResult: windcaveSnapshot,
            });
          }
          setView('confirmation');
          setCart([]);
          setPickupTime('');
          clearCartHoldTimer();
          resetCustomerForm();
          const receiptEmail = (
            orderForReceipt?.onlineCustomerEmail ||
            customerInfo.email ||
            recoveryEmail ||
            ''
          ).trim();
          const receiptName = orderForReceipt?.customerName || customerInfo.name || 'Customer';
          if (receiptEmail && orderForReceipt) {
            emailService
              .sendOrderReceipt(
                receiptEmail,
                orderForReceipt,
                receiptName,
                businessInfo
              )
              .then((result) => {
                if (!result.success && !result.skipped) {
                  console.error('Failed to send online order receipt email:', result.error);
                }
              })
              .catch((err) => {
                console.error('Error sending online order receipt email:', err);
              });
          }
        } else {
          setView('payment_interrupted');
        }
        clearWindcavePendingCheckout();
        const u = new URL(window.location.href);
        u.searchParams.delete('sessionId');
        u.searchParams.delete('id');
        u.searchParams.delete('session_id');
        u.searchParams.delete('wc_return');
        u.searchParams.delete('orderId');
        if (!u.searchParams.has('view')) {
          u.searchParams.set('view', 'online');
        }
        const qs = u.searchParams.toString();
        window.history.replaceState({}, '', u.pathname + (qs ? `?${qs}` : '') + (u.hash || ''));
      } catch (err) {
        if (!cancelled) {
          console.error('Windcave return handling failed:', err);
          setError(err instanceof Error ? err.message : 'Could not confirm payment status.');
          if (wcReturn === 'approved') {
            setView('payment_interrupted');
          } else {
            setView('menu');
          }
        }
      } finally {
        if (!cancelled) {
          setIsProcessing(false);
          setWindcaveReturnPending(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isFirebaseBackend]);

  const visibleCategories = useMemo(
    () => filterCategoriesForChannel(categories, menuItems, 'online'),
    [categories, menuItems]
  );

  const channelVisibleMenuItems = useMemo(
    () => filterMenuItemsForChannel(menuItems, categories, 'online'),
    [menuItems, categories]
  );

  // Set default category on load - prefer "Starters" if available
  useEffect(() => {
    if (visibleCategories.length === 0) return;

    const startersCategory = visibleCategories.find((c) => c.name.toLowerCase().includes('starter'));
    const defaultCategoryId = startersCategory ? startersCategory.id : visibleCategories[0].id;
    setSelectedCategoryId((prev) => {
      if (prev != null && visibleCategories.some((c) => String(c.id) === String(prev))) {
        return prev;
      }
      return defaultCategoryId;
    });
  }, [visibleCategories]);

  // Preconnect to Firebase Storage so image requests start immediately (no connection delay)
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://firebasestorage.googleapis.com';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  // Preload first 6 menu images only while browsing (skip checkout / confirmation to avoid console warnings).
  useEffect(() => {
    if (view !== 'menu') return;
    const urls = channelVisibleMenuItems
      .slice(0, 6)
      .map((item) => item.imageUrlThumb || item.imageUrl)
      .filter((url): url is string => Boolean(url) && url.includes('firebasestorage.googleapis.com'));
    if (urls.length === 0) return;
    const links: HTMLLinkElement[] = [];
    urls.forEach((href) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      link.setAttribute('data-menu-preload', '1');
      document.head.appendChild(link);
      links.push(link);
    });
    return () => links.forEach((link) => link.parentNode?.removeChild(link));
  }, [view, channelVisibleMenuItems]);

  const menuCategorySections = useMemo(
    () =>
      visibleCategories
        .map((category) => ({
          category,
          items: channelVisibleMenuItems.filter(
            (item) => String(item.categoryId) === String(category.id)
          ),
        }))
        .filter((section) => section.items.length > 0),
    [visibleCategories, channelVisibleMenuItems]
  );

  useEffect(() => {
    if (view !== 'menu') return;
    const header = menuHeaderRef.current;
    if (!header) return;

    const measureStickyOffset = () => {
      setMenuStickyOffset(header.getBoundingClientRect().height);
    };

    measureStickyOffset();
    const resizeObserver = new ResizeObserver(measureStickyOffset);
    resizeObserver.observe(header);
    window.addEventListener('resize', measureStickyOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measureStickyOffset);
    };
  }, [view, cart.length, cartHoldSecondsLeft]);

  // Highlight the category tab when that category heading reaches the sticky header bottom.
  useEffect(() => {
    if (view !== 'menu') return;

    const pickActiveCategory = () => {
      if (isManualCategoryScrollRef.current) return;
      if (menuCategorySections.length === 0) return;

      const anchor =
        menuHeaderRef.current?.getBoundingClientRect().bottom ?? menuStickyOffset;

      let activeId = String(menuCategorySections[0].category.id);

      for (const section of menuCategorySections) {
        const heading = categoryHeadingRefs.current.get(String(section.category.id));
        if (!heading) continue;
        if (heading.getBoundingClientRect().top <= anchor + 1) {
          activeId = String(section.category.id);
        }
      }

      setSelectedCategoryId((prev) => (String(prev) === activeId ? prev : activeId));
    };

    let frame = 0;
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        pickActiveCategory();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    pickActiveCategory();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
      if (manualCategoryScrollTimerRef.current) {
        window.clearTimeout(manualCategoryScrollTimerRef.current);
        manualCategoryScrollTimerRef.current = null;
      }
    };
  }, [view, menuCategorySections, menuStickyOffset]);

  // Keep the active category tab visible in the horizontal nav while scrolling.
  useEffect(() => {
    if (view !== 'menu' || selectedCategoryId == null) return;
    const tab = categoryTabRefs.current.get(String(selectedCategoryId));
    if (!tab) return;
    tab.scrollIntoView({
      behavior: isManualCategoryScrollRef.current ? 'auto' : 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [view, selectedCategoryId]);

  const addItemToCart = (item: MenuItem, selectedMeatOption?: string) => {
    if (!withinOrderingHours) return;
    const existingItem = cart.find(
      (cartItem) =>
        cartItem.id === item.id &&
        cartItem.selectedMeatOption === selectedMeatOption
    );
    if (existingItem) {
      setCart(
        cart.map((cartItem) =>
          cartItem.id === item.id &&
          cartItem.selectedMeatOption === selectedMeatOption
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      );
    } else {
      setCart([
        ...cart,
        {
          ...item,
          quantity: 1,
          status: 'pending',
          ...(selectedMeatOption ? { selectedMeatOption } : {}),
        },
      ]);
    }
  };

  const handleMeatOptionSelected = (item: MenuItem, selectedOption: string) => {
    addItemToCart(item, selectedOption);
    setMeatOptionModalItem(null);
  };

  const addToCart = (item: MenuItem) => {
    if (!withinOrderingHours) return;
    if (item.meatOptions && item.meatOptions.length > 0) {
      setMeatOptionModalItem(item);
    } else {
      addItemToCart(item);
    }
  };

  const removeFromCart = (itemId: number, selectedMeatOption?: string) => {
    setCart(
      cart.filter(
        (item) =>
          !(item.id === itemId && item.selectedMeatOption === selectedMeatOption)
      )
    );
  };

  const updateQuantity = (itemId: number, quantity: number, selectedMeatOption?: string) => {
    if (quantity <= 0) {
      removeFromCart(itemId, selectedMeatOption);
      return;
    }
    setCart(
      cart.map((item) =>
        item.id === itemId && item.selectedMeatOption === selectedMeatOption
          ? { ...item, quantity }
          : item
      )
    );
  };

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const { subtotal, taxAmount, totalAmount } = useMemo(
    () => totalsFromGstInclusiveLineItems(cart.map((item) => ({ price: item.price, quantity: item.quantity }))),
    [cart]
  );

  const handleCategoryClick = (categoryId: number | string) => {
    setSelectedCategoryId(categoryId);
    const heading = categoryHeadingRefs.current.get(String(categoryId));
    if (!heading) return;

    isManualCategoryScrollRef.current = true;
    if (manualCategoryScrollTimerRef.current) {
      window.clearTimeout(manualCategoryScrollTimerRef.current);
    }
    heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
    manualCategoryScrollTimerRef.current = window.setTimeout(() => {
      isManualCategoryScrollRef.current = false;
      manualCategoryScrollTimerRef.current = null;
    }, 700);
  };

  const renderMenuItemCard = (item: MenuItem) => {
    const hasOptions = item.meatOptions && item.meatOptions.length > 0;
    const cartItem = cart.find((ci) => ci.id === item.id && !ci.selectedMeatOption);
    const quantityInCart = cartItem ? cartItem.quantity : 0;

    return (
      <div
        key={item.id}
        className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col"
      >
        <div className="relative h-32 bg-gray-200">
          {(() => {
            const imgUrl = item.imageUrlThumb || item.imageUrl;
            const isOurImage = imgUrl && imgUrl.includes('firebasestorage.googleapis.com');
            if (!imgUrl || !isOurImage) {
              return (
                <div className="w-full h-32 flex items-center justify-center text-gray-500 text-sm bg-gray-100">
                  No photo
                </div>
              );
            }
            return (
              <img
                src={imgUrl}
                alt={item.name}
                width={320}
                height={128}
                loading="lazy"
                decoding="async"
                className="w-full h-32 object-cover bg-gray-200"
              />
            );
          })()}
          {item.isSignatureDish && (
            <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center">
              <StarSolidIcon className="w-3 h-3 mr-1" />
              Signature
            </div>
          )}
        </div>
        <div className="p-3 flex flex-col h-full">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800 mb-1">{item.name}</h3>
            {item.description && (
              <>
                <p className="text-sm text-gray-600 mb-1 line-clamp-3">
                  {item.description}
                </p>
                <button
                  type="button"
                  onClick={() => setDetailItem(item)}
                  className="text-xs text-pink-600 hover:text-pink-700 font-semibold underline mb-1"
                >
                  More details
                </button>
              </>
            )}
            {item.allergens && item.allergens.length > 0 && (
              <div className="mb-1">
                <div className="flex flex-wrap gap-1">
                  {item.allergens.map((allergen, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200"
                    >
                      ⚠️ {allergen}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {item.allergyWarning && (
              <div className="mb-1 p-1.5 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                <strong>⚠️ Warning:</strong> {item.allergyWarning}
              </div>
            )}
            <p className="text-pink-600 font-bold text-base sm:text-lg mt-1">
              ${item.price.toFixed(2)}
            </p>
          </div>
          <div className="mt-3">
            {hasOptions ? (
              <button
                type="button"
                onClick={() => addToCart(item)}
                disabled={!withinOrderingHours}
                className={`w-full rounded-lg py-2 font-semibold transition-colors ${
                  withinOrderingHours
                    ? 'bg-pink-500 text-white hover:bg-pink-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {withinOrderingHours ? 'Choose Option' : 'Ordering closed'}
              </button>
            ) : quantityInCart > 0 ? (
              <div className="flex items-center justify-center space-x-2">
                <button
                  type="button"
                  onClick={() => updateQuantity(item.id, quantityInCart - 1)}
                  className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                >
                  <span className="text-gray-600">−</span>
                </button>
                <span className="w-10 text-center font-semibold">{quantityInCart}</span>
                <button
                  type="button"
                  onClick={() =>
                    withinOrderingHours && updateQuantity(item.id, quantityInCart + 1)
                  }
                  disabled={!withinOrderingHours}
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    withinOrderingHours
                      ? 'bg-gray-200 hover:bg-gray-300'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <span className="text-gray-600">+</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => addToCart(item)}
                disabled={!withinOrderingHours}
                className={`w-full rounded-lg py-2 font-semibold transition-colors ${
                  withinOrderingHours
                    ? 'bg-pink-500 text-white hover:bg-pink-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {withinOrderingHours ? 'Add to Cart' : 'Ordering closed'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const generatePickupOptions = () => {
    const now = new Date();
    if (usingSchedule) {
      return listPickupHHmm(now, todayPeriods, scheduleTimezone).map((value) => ({
        value,
        label: formatOrderingTime(value),
      }));
    }
    const options: { value: string; label: string }[] = [];
    // Minimum 30 minutes from now
    const start = new Date(now.getTime() + 30 * 60 * 1000);
    // Round up to the next 15-minute slot
    const roundedMinutes = Math.ceil(start.getMinutes() / 15) * 15;
    start.setMinutes(roundedMinutes, 0, 0);

    // Until online ordering closes for this service day (or 23:45 if hours are open-ended)
    let end = getPickupSlotsEndDate(now, orderingStart, orderingEnd);
    if (end <= start) {
      end = new Date(start.getTime() + 3 * 60 * 60 * 1000);
    }

    for (let t = new Date(start); t <= end; t.setMinutes(t.getMinutes() + 15)) {
      const hh = t.getHours().toString().padStart(2, '0');
      const mm = t.getMinutes().toString().padStart(2, '0');
      const value = `${hh}:${mm}`;
      const label = t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      options.push({ value, label });
    }

    return options;
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      setError('Your cart is empty');
      return;
    }
    setView('checkout');
    setError(null);
  };

  const startWindcaveHppRedirect = async (orderDoc: Order) => {
    const { collectWindcaveBrowserHints, callWindcaveCreateSession } = await import('../lib/onlineOrderingApi');
    const oid = orderDoc.id;
    if (!oid) {
      throw new Error('Missing order id');
    }
    const email = (orderDoc.onlineCustomerEmail || customerInfo.email || '').trim();
    const fullName = (orderDoc.customerName || customerInfo.name || '').trim();
    const nameParts = fullName.split(/\s+/).filter(Boolean);
    const firstName = (nameParts[0] || 'Customer').slice(0, 80);
    const lastName = (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '').slice(0, 80);
    const phoneNumber = (orderDoc.onlineCustomerPhone || customerInfo.phone || '').trim();
    const streetAddress = (
      orderDoc.onlineBillingAddress || customerInfo.streetAddress || ''
    ).trim();
    const city = (orderDoc.onlineBillingCity || customerInfo.city || 'Auckland').trim();
    const postcode = (orderDoc.onlineBillingPostcode || customerInfo.postcode || '').trim();
    const { hppUrl, sessionId: wcSessionId, methodsRequested } = await callWindcaveCreateSession({
      orderId: oid,
      browser: collectWindcaveBrowserHints(),
      customer: {
        email,
        firstName,
        ...(lastName ? { lastName } : {}),
        phoneNumber,
        billing: {
          name: fullName.slice(0, 120),
          address1: streetAddress,
          city,
          postalCode: postcode,
          countryCode: 'NZ',
          phoneNumber,
        },
      },
      methods: WINDCAVE_HPP_SESSION_METHODS,
    });
    if (import.meta.env.DEV && methodsRequested?.length) {
      console.info('[Windcave] HPP methods sent to session create:', methodsRequested.join(', '));
    }
    try {
      persistWindcavePendingCheckout(wcSessionId, orderDoc);
    } catch {
      // ignore quota / private mode
    }
    window.location.href = hppUrl;
  };

  const handleRetryWindcavePayment = async () => {
    const oid = orderId ?? placedOrder?.id;
    if (!oid || !isFirebaseBackend || !placedOrder || placedOrder.id !== oid) {
      setError('Could not restore your order for payment. Please contact us with your order ID.');
      return;
    }
    setError(null);
    setIsProcessing(true);
    setFirebaseCheckoutPhase('opening_payment');
    try {
      await startWindcaveHppRedirect(placedOrder);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Could not restart payment.');
      setIsProcessing(false);
      setFirebaseCheckoutPhase(null);
    }
  };

  const handleAbandonOnlineCheckout = async () => {
    const oid = orderId ?? placedOrder?.id;
    const em = (placedOrder?.onlineCustomerEmail || customerInfo.email || '').trim();
    if (!oid) {
      setError('Missing order reference. Please contact us with your order number.');
      return;
    }
    if (!em) {
      setError('We could not verify the email on this order. Please contact the restaurant to cancel.');
      return;
    }
    if (!window.confirm('Cancel this unpaid order? You can browse the menu and start again.')) return;
    setIsProcessing(true);
    setError(null);
    try {
      const { callAbandonUnpaidOnlineOrder } = await import('../lib/onlineOrderingApi');
      await callAbandonUnpaidOnlineOrder({ orderId: oid, customerEmail: em });
      setView('menu');
      setCart([]);
      setOrderId(null);
      setPlacedOrder(null);
      setWindcaveResult(null);
      setPaymentMethod(null);
      setPickupTime('');
      resetCustomerForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel order.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePlaceOrder = async () => {
    // Validate pickup time (must be at least 30 minutes from now)
    if (!pickupTime) {
      setError('Please choose a pickup time for your order.');
      return;
    }
    if (isFirebaseBackend && isCustomerAppAuthConfigured() && !customerAppUser) {
      setError('Please sign in or create an account to place your order.');
      return;
    }
    {
      const pickupMinutes = parseTimeToMinutes(pickupTime);
      if (pickupMinutes == null) {
        setError('Please choose a valid pickup time.');
        return;
      }
      const now = new Date();
      const min = new Date(now.getTime() + 30 * 60 * 1000);
      if (usingSchedule) {
        if (!isMinutesInPeriods(pickupMinutes, todayPeriods, true)) {
          setError('Choose a pickup time during opening hours.');
          return;
        }
        const selected = zonedTodayAtMinutes(now, pickupMinutes, scheduleTimezone);
        if (selected.getTime() < min.getTime()) {
          setError('Pickup time must be at least 30 minutes from now.');
          return;
        }
      } else {
        const [ph, pm] = pickupTime.split(':').map(Number);
        const selected = new Date();
        selected.setHours(ph, pm, 0, 0);
        if (selected < min) {
          setError('Pickup time must be at least 30 minutes from now.');
          return;
        }
      }
    }

    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      setError('Please fill in all required customer information');
      return;
    }

    if (
      isFirebaseBackend &&
      customerAppUser?.email &&
      customerInfo.email.trim().toLowerCase() !== customerAppUser.email.trim().toLowerCase()
    ) {
      setError('Checkout email must match your signed-in account.');
      return;
    }

    if (isFirebaseBackend) {
      if (!customerInfo.streetAddress.trim()) {
        setError('Please enter your street address for card payment.');
        return;
      }
      if (!customerInfo.postcode.trim()) {
        setError('Please enter your postcode for card payment.');
        return;
      }
    }

    if (!isFirebaseBackend && !paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setFirebaseCheckoutPhase(null);

    try {
      // Build notes string only if provided (avoid undefined fields in Firestore)
      const trimmedNotes = customerInfo.specialInstructions.trim();
      const orderNotes = trimmedNotes.length > 0 ? trimmedNotes : undefined;

      // Sanitize items to keep Firestore document small and free of undefined values
      const sanitizedItems: OrderItem[] = cart.map((item) => {
        const base: any = {
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          status: item.status,
        };

        // Only include notes if present (trimmed and length > 0)
        if (item.notes && item.notes.trim().length > 0) {
          base.notes = item.notes.trim().slice(0, 500);
        }

        // Only include selected meat option if present
        if (item.selectedMeatOption) {
          base.selectedMeatOption = item.selectedMeatOption;
        }

        return base as OrderItem;
      });

      // Match server `placeOnlineOrder`: totals from the exact payload lines (GST-inclusive).
      const payloadTotals = totalsFromGstInclusiveLineItems(
        sanitizedItems.map((item) => ({ price: item.price, quantity: item.quantity }))
      );

      // Calculate pickup timestamp (validated earlier; required for online orders)
      const pickupMinutes = parseTimeToMinutes(pickupTime);
      const pickupTimestamp = usingSchedule && pickupMinutes != null
        ? zonedTodayAtMinutes(new Date(), pickupMinutes, scheduleTimezone).getTime()
        : (() => {
            const [ph, pm] = pickupTime.split(':').map(Number);
            const dt = new Date();
            dt.setHours(ph, pm, 0, 0);
            return dt.getTime();
          })();

      if (isFirebaseBackend) {
        const { callPlaceOnlineOrder } = await import('../lib/onlineOrderingApi');
        setFirebaseCheckoutPhase('saving_order');
        const { orderId: placedId, order: created } = await callPlaceOnlineOrder({
          items: sanitizedItems,
          customerName: customerInfo.name.trim(),
          customerEmail: customerInfo.email.trim(),
          customerPhone: customerInfo.phone.trim(),
          billingAddress: customerInfo.streetAddress.trim(),
          billingCity: customerInfo.city.trim() || 'Auckland',
          billingPostcode: customerInfo.postcode.trim(),
          notes: orderNotes,
          pickupTime: pickupTimestamp,
          subtotal: payloadTotals.subtotal,
          taxAmount: payloadTotals.taxAmount,
          totalAmount: payloadTotals.totalAmount,
          paymentMethod: null,
        });
        setOrderId(placedId);
        setPlacedOrder(created);
        clearCartHoldTimer();

        setFirebaseCheckoutPhase('opening_payment');
        try {
          await startWindcaveHppRedirect(created);
          return;
        } catch (werr) {
          console.error('Windcave create session failed:', werr);
          throw werr instanceof Error
            ? werr
            : new Error('Could not start secure payment. Please try again or contact us.');
        }
      } else {
        // Calculate order totals (mock / non-Firestore data source)
        const orderData: Omit<Order, 'id'> = {
          orderType: 'online',
          items: sanitizedItems,
          status: 'new',
          timestamp: Date.now(),
          paymentStatus: 'unpaid',
          payments: [],
          ...(customerInfo.name.trim() ? { customerName: customerInfo.name.trim() } : {}),
          ...(orderNotes ? { notes: orderNotes } : {}),
          pickupTime: pickupTimestamp,
          subtotal: payloadTotals.subtotal,
          taxAmount: payloadTotals.taxAmount,
          totalAmount: payloadTotals.totalAmount,
          orderBatches: [
            {
              batchId: `BATCH-${Date.now()}`,
              timestamp: Date.now(),
              itemIndices: cart.map((_, index) => index),
            },
          ],
        };

        const newOrder = await createOrder(orderData);
        setOrderId(newOrder.id);
        setPlacedOrder(newOrder);

        if (customerInfo.email) {
          emailService
            .sendOrderReceipt(
              customerInfo.email,
              newOrder,
              customerInfo.name || 'Customer',
              businessInfo
            )
            .then((result) => {
              if (!result.success && !result.skipped) {
                console.error('Failed to send online order receipt email:', result.error);
              }
            })
            .catch((err) => {
              console.error('Error sending online order receipt email:', err);
            });
        }

        if (paymentMethod === 'card_online') {
          await handleCardPayment(newOrder.id);
        } else if (paymentMethod === 'account2account') {
          await handleAccount2AccountPayment(newOrder.id);
        }

        setView('confirmation');
      }
    } catch (err) {
      console.error('Error placing order:', err);
      setError(err instanceof Error ? err.message : 'Failed to place order. Please try again.');
    } finally {
      setIsProcessing(false);
      setFirebaseCheckoutPhase(null);
    }
  };

  const handleCardPayment = async (orderId: string) => {
    // For card payments, we'll integrate with a payment gateway (Stripe, etc.)
    // For now, we'll create a pending payment that can be processed manually
    const payment: Payment = {
      method: 'card_online',
      amount: totalAmount,
      timestamp: Date.now(),
      paymentGateway: 'stripe',
      paymentStatus: 'pending',
    };

    await ordersService.update(orderId, {
      payments: [payment],
      paymentStatus: 'unpaid', // Will be updated when payment is confirmed
    });

    // TODO: Integrate with payment gateway:
    // 1. Create a Payment Intent with Stripe or other gateway
    // 2. Redirect to payment gateway checkout
    // 3. Handle payment confirmation webhook
    // 4. Update order status when payment succeeds
    
    // For now, show payment instructions
    setView('payment');
  };

  const handleAccount2AccountPayment = async (orderId: string) => {
    // Generate a payment reference
    const reference = `A2A-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    const payment: Payment = {
      method: 'account2account',
      amount: totalAmount,
      timestamp: Date.now(),
      paymentGateway: 'account2account',
      paymentStatus: 'pending',
      accountNumber: reference, // Use accountNumber field for reference
    };

    await ordersService.update(orderId, {
      payments: [payment],
      paymentStatus: 'unpaid', // Will be updated manually when payment is received
    });

    // Show payment instructions
    setView('payment');
  };

  if (windcaveReturnPending) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50 px-4">
        <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-pink-500 mb-4" />
        <p className="text-lg font-medium text-gray-800 text-center">Confirming your payment…</p>
        <p className="text-sm text-gray-600 text-center mt-2 max-w-sm">
          One moment while we verify with the payment provider.
        </p>
      </div>
    );
  }

  if (windcaveReturnBlocked && error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-pink-50 to-rose-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-pink-100 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Could not confirm payment</h2>
          <p className="text-sm text-gray-700 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => {
              setWindcaveReturnBlocked(false);
              setError(null);
              const u = new URL(window.location.href);
              u.searchParams.delete('wc_return');
              u.searchParams.delete('orderId');
              const qs = u.searchParams.toString();
              window.history.replaceState({}, '', u.pathname + (qs ? `?${qs}` : '') + (u.hash || ''));
            }}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 rounded-lg"
          >
            Continue to menu
          </button>
        </div>
      </div>
    );
  }

  if (view === 'confirmation') {
    let payStatusLabel: string;
    if (windcaveResult != null && windcaveResult.authorised) {
      payStatusLabel = 'Paid (card)';
    } else if (placedOrder?.paymentStatus === 'paid') {
      payStatusLabel = 'Paid';
    } else if (placedOrder?.paymentStatus === 'unpaid') {
      payStatusLabel = 'Payment pending';
    } else {
      payStatusLabel = placedOrder?.paymentStatus || '—';
    }

    const pickupDisplay = formatPickupDisplay(placedOrder?.pickupTime, pickupTime);
    const restaurantAddress = businessInfo?.address?.trim() || '';
    const restaurantName = businessInfo?.businessName?.trim() || '';
    const restaurantPhone = businessInfo?.phone?.trim() || '';
    const restaurantEmail = businessInfo?.email?.trim() || '';
    const customerContactEmail = (placedOrder?.onlineCustomerEmail || customerInfo.email || '').trim();
    const phoneHref = restaurantPhone ? restaurantPhone.replace(/[^\d+]/g, '') : '';

    const showPickupCard =
      Boolean(pickupDisplay || restaurantAddress || restaurantPhone || restaurantEmail || customerContactEmail);

    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
          <CheckCircleIcon className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Order placed</h2>
          <p className="text-gray-600 mb-2">Thank you for your order</p>
          {orderId && <p className="text-sm text-gray-500 mb-1">Order ID: {orderId}</p>}
          <p className="text-sm font-medium text-gray-800 mb-4">
            Payment status: <span className="text-pink-600">{payStatusLabel}</span>
          </p>

          {showPickupCard ? (
            <div className="text-left border border-pink-100 rounded-lg p-4 mb-4 bg-pink-50/60">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pickup</h3>
              {pickupDisplay ? (
                <p className="text-sm text-gray-900 mb-2">
                  <span className="text-gray-600">Pickup time: </span>
                  <span className="font-semibold">{pickupDisplay}</span>
                </p>
              ) : null}
              {restaurantAddress ? (
                <div className="text-sm text-gray-800">
                  {restaurantName ? <p className="font-semibold text-gray-900 mb-1">{restaurantName}</p> : null}
                  <p className="whitespace-pre-line leading-relaxed">{restaurantAddress}</p>
                </div>
              ) : restaurantName ? (
                <p className="text-sm font-semibold text-gray-900">{restaurantName}</p>
              ) : null}
              {restaurantPhone ? (
                <p className="text-sm text-gray-800 mt-2">
                  <span className="text-gray-600">Phone: </span>
                  {phoneHref ? (
                    <a href={`tel:${phoneHref}`} className="font-semibold text-pink-700 hover:underline">
                      {restaurantPhone}
                    </a>
                  ) : (
                    <span className="font-semibold">{restaurantPhone}</span>
                  )}
                </p>
              ) : null}
              {restaurantEmail ? (
                <p className="text-sm text-gray-800 mt-2 break-all">
                  <span className="text-gray-600">Restaurant email: </span>
                  <a href={`mailto:${restaurantEmail}`} className="font-semibold text-pink-700 hover:underline">
                    {restaurantEmail}
                  </a>
                </p>
              ) : null}
              {customerContactEmail ? (
                <p className="text-sm text-gray-700 mt-2 break-all">
                  <span className="text-gray-600">Your email (confirmation): </span>
                  <span className="font-medium">{customerContactEmail}</span>
                </p>
              ) : null}
            </div>
          ) : null}

          {placedOrder?.items && placedOrder.items.length > 0 && (
            <div className="text-left border border-gray-100 rounded-lg p-4 mb-4 bg-gray-50/80">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Your order</h3>
              <ul className="space-y-2 text-sm text-gray-800">
                {placedOrder.items.map((line, idx) => (
                  <li key={`${line.id}-${idx}`} className="flex justify-between gap-2">
                    <span className="min-w-0">
                      {line.name}
                      {line.selectedMeatOption ? (
                        <span className="text-gray-500"> ({line.selectedMeatOption})</span>
                      ) : null}
                      <span className="text-gray-500"> ×{line.quantity}</span>
                    </span>
                    <span className="shrink-0 font-medium">${(line.price * line.quantity).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              {typeof placedOrder.subtotal === 'number' && (
                <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-700 space-y-1">
                  <div className="flex justify-between">
                    <span>Subtotal (ex GST)</span>
                    <span>${placedOrder.subtotal.toFixed(2)}</span>
                  </div>
                  {typeof placedOrder.taxAmount === 'number' && (
                    <div className="flex justify-between">
                      <span>GST (15%)</span>
                      <span>${placedOrder.taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-gray-900 pt-1">
                    <span>Total</span>
                    <span>
                      $
                      {(placedOrder.totalAmount ??
                        (placedOrder.subtotal ?? 0) + (placedOrder.taxAmount ?? 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="text-gray-700 mb-6 text-sm leading-relaxed">
            {windcaveResult?.authorised ? (
              <>
                Your card payment was <span className="font-semibold text-green-700">approved</span>. Thank you.
                {windcaveResult.responseText ? (
                  <span className="block text-sm text-gray-500 mt-2">{windcaveResult.responseText}</span>
                ) : null}
              </>
            ) : !isFirebaseBackend && paymentMethod === 'account2account' ? (
              'Please complete your payment using the instructions provided. Your order will be processed once payment is confirmed.'
            ) : (
              'Your order is being processed. You will receive a confirmation email shortly.'
            )}
          </p>
          <button
            type="button"
            onClick={() => {
              clearOnlineConfirmationState();
              resetAllergyGateForNewOrder();
              setView('menu');
              setCart([]);
              setPickupTime('');
              resetCustomerForm();
              setPaymentMethod(null);
              setOrderId(null);
              setPlacedOrder(null);
              setWindcaveResult(null);
            }}
            className="w-full bg-pink-500 text-white rounded-lg py-3 font-semibold hover:bg-pink-600 transition-colors"
          >
            Place another order
          </button>
        </div>
      </div>
    );
  }

  if (view === 'payment_interrupted') {
    const failTotal =
      typeof placedOrder?.totalAmount === 'number'
        ? placedOrder.totalAmount
        : (placedOrder?.subtotal ?? 0) + (placedOrder?.taxAmount ?? 0);
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-amber-50 to-rose-50 px-4 py-8">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-amber-100 text-center">
          <XCircleIcon className="w-16 h-16 text-amber-500 mx-auto mb-4" aria-hidden />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment not completed</h2>
          <p className="text-sm text-gray-600 mb-1">
            Your order is saved, but the kitchen is not notified until payment succeeds.
          </p>
          {orderId && <p className="text-xs text-gray-500 mb-4">Order ID: {orderId}</p>}
          {windcaveResult?.responseText ? (
            <p className="text-sm font-medium text-gray-800 mb-4 rounded-lg bg-gray-50 px-3 py-2 border border-gray-200">
              {windcaveResult.responseText}
            </p>
          ) : null}
          {placedOrder?.items && placedOrder.items.length > 0 ? (
            <div className="text-left border border-gray-100 rounded-lg p-3 mb-4 bg-gray-50/80 text-sm text-gray-800">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Your order</p>
              <ul className="space-y-1">
                {placedOrder.items.map((line, idx) => (
                  <li key={`${line.id}-${idx}`} className="flex justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {line.name}
                      <span className="text-gray-500"> ×{line.quantity}</span>
                    </span>
                    <span className="shrink-0">${(line.price * line.quantity).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 pt-2 border-t border-gray-200 font-semibold text-right">
                Total ${failTotal.toFixed(2)}
              </p>
            </div>
          ) : null}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm text-left">{error}</div>
          )}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => void handleRetryWindcavePayment()}
              disabled={isProcessing}
              className="w-full bg-pink-600 hover:bg-pink-700 disabled:bg-gray-400 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              {isProcessing && firebaseCheckoutPhase === 'opening_payment'
                ? 'Opening secure payment…'
                : 'Try payment again'}
            </button>
            <button
              type="button"
              onClick={() => void handleAbandonOnlineCheckout()}
              disabled={isProcessing}
              className="w-full border border-gray-300 text-gray-800 font-medium py-3 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel this order
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-6">
            You will complete card payment on Windcave&apos;s secure payment page.
          </p>
        </div>
      </div>
    );
  }

  if (menuLoading && view !== 'mealPlans') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (view === 'mealPlans' && (businessInfo?.mealPlansEnabled === true || mealPlanReturnMessage)) {
    return (
      <OnlineMealPlanPanel
        paymentReturnMessage={mealPlanReturnMessage}
        onBack={() => {
          setMealPlanReturnMessage(null);
          setView('menu');
        }}
      />
    );
  }

  // Global food allergy warning — must be acknowledged before ordering (after checkout confirmation screens).
  if (!hasAcceptedAllergyWarning) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-rose-50 px-4">
        <div className="max-w-xl w-full bg-white rounded-2xl shadow-2xl p-8 border border-pink-100">
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 text-center">
            Important Food Allergy Notice
          </h1>
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0 mr-3 mt-1">
              <svg className="h-8 w-8 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92C18.078 14.538 17.115 16 15.586 16H4.414c-1.53 0-2.493-1.462-1.737-2.981l5.58-9.92zM11 14a1 1 0 11-2 0 1 1 0 012 0zm-1-7a1 1 0 00-1 1v3a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm md:text-base text-gray-800 leading-relaxed">
                <strong>Kings Park advises</strong> that food prepared here may contain or have come in contact with
                <span className="font-semibold"> peanuts, tree nuts, soybean, milk, and wheat</span>, as well as other
                common allergens.
              </p>
              <p className="mt-3 text-xs md:text-sm text-gray-600">
                If you or anyone in your group has a food allergy or intolerance, please contact the restaurant directly
                before placing an order, and clearly mention this in the special instructions when ordering.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setHasAcceptedAllergyWarning(true);
              try {
                window.localStorage.setItem(ONLINE_ORDER_ALLERGY_ACCEPTED_KEY, '1');
                window.sessionStorage.setItem(ONLINE_ORDER_ALLERGY_ACCEPTED_KEY, '1');
              } catch {
                // ignore
              }
            }}
            className="mt-6 w-full bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 rounded-lg shadow-md transition-colors"
          >
            Read and Understood – Continue to Menu
          </button>
        </div>
      </div>
    );
  }

  if (view === 'payment') {
    const currentOrder = placedOrder ?? (orderId ? orders.find((o) => o.id === orderId) : null);
    const payment = currentOrder?.payments?.[0] as Payment | undefined;
    
    if (!isFirebaseBackend && paymentMethod === 'account2account') {
      return (
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => setView('checkout')}
              className="mb-4 flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Checkout
            </button>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Account2Account Payment</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Amount to Pay</p>
                  <p className="text-3xl font-bold text-pink-600">${totalAmount.toFixed(2)}</p>
                </div>
                
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-2">Payment Reference</p>
                  <p className="text-lg font-mono font-semibold bg-gray-100 p-3 rounded">
                    {payment?.accountNumber || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Please include this reference when making your payment
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Bank Account Details</p>
                  <div className="bg-gray-50 p-4 rounded space-y-2">
                    <p className="text-sm"><span className="font-semibold">Account Name:</span> Kings Park Restaurant</p>
                    <p className="text-sm"><span className="font-semibold">Account Number:</span> 12-3456-7890123-45</p>
                    <p className="text-sm"><span className="font-semibold">Bank:</span> [Your Bank Name]</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Please update these details in the settings or configuration
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600">
                    Once you've completed the payment, your order will be processed. 
                    You'll receive a confirmation email once payment is verified.
                  </p>
                </div>

                <button
                  onClick={() => setView('confirmation')}
                  className="w-full bg-pink-500 text-white rounded-lg py-3 font-semibold hover:bg-pink-600 transition-colors mt-6"
                >
                  I've Completed the Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (paymentMethod === 'card_online') {
      return (
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => setView('checkout')}
              className="mb-4 flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Checkout
            </button>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Card payment</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Amount to Pay</p>
                  <p className="text-3xl font-bold text-pink-600">${totalAmount.toFixed(2)}</p>
                </div>

                <div className="border-t pt-4">
                  {isFirebaseBackend ? (
                    <p className="text-sm text-gray-600">
                      You will be redirected to our secure payment page (Windcave) when you place your order. If you
                      returned here without paying, go back to checkout and try again.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-4">
                        Card payment integration is being set up. For now, please use Account2Account payment or contact
                        us directly.
                      </p>
                      <p className="text-xs text-gray-500">
                        Once payment gateway integration is complete, you will be able to securely pay with your card
                        here.
                      </p>
                    </>
                  )}
                </div>

                {!isFirebaseBackend && (
                  <button
                    onClick={() => {
                      setPaymentMethod('account2account');
                      handleAccount2AccountPayment(orderId!);
                    }}
                    className="w-full bg-pink-500 text-white rounded-lg py-3 font-semibold hover:bg-pink-600 transition-colors mt-6"
                  >
                    Use Account2Account Instead
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Get all unique allergens from cart items
  const getAllAllergens = () => {
    const allergenSet = new Set<string>();
    cart.forEach(item => {
      if (item.allergens) {
        item.allergens.forEach(allergen => allergenSet.add(allergen));
      }
    });
    return Array.from(allergenSet);
  };

  const cartAllergens = getAllAllergens();

  if (view === 'checkout') {
    return (
      <>
        {isFirebaseBackend && firebaseCheckoutPhase && isProcessing ? (
          <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm px-6 text-center">
            <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-pink-500 mb-4" />
            <p className="text-lg font-semibold text-gray-900">
              {firebaseCheckoutPhase === 'saving_order' ? 'Saving your order…' : 'Opening secure payment…'}
            </p>
            <p className="text-sm text-gray-600 mt-2 max-w-xs">
              This step can take a few seconds. Please keep this page open until you are redirected.
            </p>
          </div>
        ) : null}
        <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => setView('cart')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-800"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Cart
          </button>

          {/* Pickup time summary */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Pickup time:</span>{' '}
              {pickupTime || 'Not set'}
            </p>
          </div>

          {isFirebaseBackend && !isCustomerAppAuthConfigured() && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-300 text-amber-900 rounded text-sm">
              Online ordering login is not configured (missing customer app Firebase env). Staff: set
              VITE_CUSTOMER_APP_FIREBASE_* and rebuild.
            </div>
          )}

          {isFirebaseBackend && isCustomerAppAuthConfigured() && !customerAppAuthReady && (
            <div className="mb-6 p-6 bg-white rounded-lg shadow text-center text-gray-600">Checking sign-in…</div>
          )}

          {isFirebaseBackend &&
            isCustomerAppAuthConfigured() &&
            customerAppAuthReady &&
            !customerAppUser && (
              <CustomerAppAuthPanel
                initialEmail={customerInfo.email}
                initialName={customerInfo.name}
                onAuthenticated={() => setError(null)}
              />
            )}

          {(!isFirebaseBackend ||
            (isCustomerAppAuthConfigured() && customerAppAuthReady && Boolean(customerAppUser))) && (
          <>
          {customerAppUser && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-4 py-3">
              <span>
                Signed in as <span className="font-medium">{customerAppUser.email}</span>
              </span>
              <button
                type="button"
                className="text-pink-600 hover:underline"
                onClick={() => void signOutCustomerApp()}
              >
                Sign out
              </button>
            </div>
          )}

          {/* Allergy Warning Banner */}
          {cartAllergens.length > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Allergy Warning
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p className="mb-2">Your order contains items with the following allergens:</p>
                    <div className="flex flex-wrap gap-2">
                      {cartAllergens.map((allergen, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200"
                        >
                          ⚠️ {allergen}
                        </span>
                      ))}
                    </div>
                    <p className="mt-2 font-semibold">Please inform us of any allergies in the special instructions below.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Customer Information</h2>
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 disabled:bg-gray-100"
                  required
                  readOnly={Boolean(customerAppUser)}
                  disabled={Boolean(customerAppUser)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>

              {isFirebaseBackend ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customerInfo.streetAddress}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, streetAddress: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                      placeholder="Used to pre-fill secure card payment"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        value={customerInfo.city}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        placeholder="Auckland"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Postcode <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={customerInfo.postcode}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, postcode: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        required
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Your name, address, and phone are sent to Windcave so you do not have to enter them again on the card page.
                  </p>
                </>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Instructions for Order
                </label>
                <textarea
                  value={customerInfo.specialInstructions}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, specialInstructions: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  placeholder="Any special instructions for your order? (e.g., allergies, dietary needs, preparation notes)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Please clearly mention any food allergies or dietary requirements here.
                </p>
              </div>
            </div>
          </div>

          {!isFirebaseBackend ? (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment method</h2>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('card_online')}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                    paymentMethod === 'card_online'
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center">
                    <CreditCardIcon className="w-6 h-6 mr-3 text-gray-600" />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">Pay with card</p>
                      <p className="text-xs text-gray-500 mt-1">Demo / local checkout flow</p>
                    </div>
                    {paymentMethod === 'card_online' && (
                      <CheckCircleIcon className="w-6 h-6 text-pink-500" />
                    )}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('account2account')}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                    paymentMethod === 'account2account'
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-6 h-6 mr-3 flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">Account2Account</p>
                      <p className="text-xs text-gray-500 mt-1">Manual bank instructions (non-production)</p>
                    </div>
                    {paymentMethod === 'account2account' && (
                      <CheckCircleIcon className="w-6 h-6 text-pink-500" />
                    )}
                  </div>
                </button>
              </div>
            </div>
          ) : null}

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-600">Subtotal (ex GST)</span>
              <span className="text-gray-800 font-semibold">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-600">GST (15%)</span>
              <span className="text-gray-800 font-semibold">${taxAmount.toFixed(2)}</span>
            </div>
            <div className="border-t pt-4 flex justify-between items-center">
              <span className="text-xl font-bold text-gray-800">Total</span>
              <span className="text-2xl font-bold text-pink-600">${totalAmount.toFixed(2)}</span>
            </div>
            <button
              type="button"
              onClick={handlePlaceOrder}
              disabled={
                isProcessing ||
                !withinOrderingHours ||
                (!isFirebaseBackend && !paymentMethod) ||
                (isFirebaseBackend && isCustomerAppAuthConfigured() && !customerAppUser)
              }
              className="w-full mt-6 bg-pink-500 text-white rounded-lg py-3 font-semibold hover:bg-pink-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isProcessing
                ? firebaseCheckoutPhase === 'saving_order'
                  ? 'Saving your order…'
                  : firebaseCheckoutPhase === 'opening_payment'
                    ? 'Opening secure payment…'
                    : 'Processing…'
                : isFirebaseBackend
                  ? 'Place order & continue to secure payment'
                  : 'Place order'}
            </button>
          </div>
          </>
          )}
        </div>
      </div>
      </>
    );
  }

  if (view === 'cart') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        {orderingHoursRestricted && !withinOrderingHours && (
          <div className="bg-amber-500 text-amber-900 px-4 py-3 text-center font-semibold text-sm sm:text-base rounded-lg mb-4 max-w-4xl mx-auto">
            {closedHoursText} Checkout is closed until the next open window.
          </div>
        )}
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Your Cart</h1>
            <div className="flex flex-col items-end gap-1">
              {cartHoldSecondsLeft != null && cartHoldSecondsLeft > 0 ? (
                <p
                  className={`text-sm font-semibold tabular-nums ${
                    cartHoldSecondsLeft <= 120 ? 'text-red-600' : 'text-amber-700'
                  }`}
                >
                  Cart expires in {formatCartHoldLabel(cartHoldSecondsLeft)}
                </p>
              ) : null}
              <button
                onClick={() => setView('menu')}
                className="flex items-center text-gray-600 hover:text-gray-800"
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                Continue Shopping
              </button>
            </div>
          </div>

          {cart.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <ShoppingCartIcon className="w-24 h-24 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-600 mb-4">Your cart is empty</p>
              <button
                onClick={() => setView('menu')}
                className="bg-pink-500 text-white rounded-lg px-6 py-3 font-semibold hover:bg-pink-600 transition-colors"
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}
              {/* Allergy Warning Banner in Cart */}
              {cartAllergens.length > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Allergy Warning
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p className="mb-2">Your cart contains items with allergens:</p>
                        <div className="flex flex-wrap gap-2">
                          {cartAllergens.map((allergen, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200"
                            >
                              ⚠️ {allergen}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                {cart.map((item) => (
                  <div key={item.id} className="py-4 border-b last:border-b-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{item.name}</h3>
                        {(item.allergens && item.allergens.length > 0) && (
                          <div className="mt-1 mb-1">
                            <div className="flex flex-wrap gap-1">
                              {item.allergens.map((allergen, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200"
                                >
                                  ⚠️ {allergen}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {item.allergyWarning && (
                          <div className="mt-1 mb-1 p-1.5 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                            <strong>⚠️ Warning:</strong> {item.allergyWarning}
                          </div>
                        )}
                        <p className="text-gray-600">${item.price.toFixed(2)} each</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.id,
                                item.quantity - 1,
                                item.selectedMeatOption
                              )
                            }
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                          >
                            <span className="text-gray-600">−</span>
                          </button>
                          <span className="w-12 text-center font-semibold">{item.quantity}</span>
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.id,
                                item.quantity + 1,
                                item.selectedMeatOption
                              )
                            }
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                          >
                            <span className="text-gray-600">+</span>
                          </button>
                        </div>
                        <p className="w-24 text-right font-semibold">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        <button
                          onClick={() => removeFromCart(item.id, item.selectedMeatOption)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Selected meat option (if any) */}
                    {item.selectedMeatOption && (
                      <p className="mt-2 text-xs text-gray-600">
                        <span className="font-semibold">Option:</span> {item.selectedMeatOption}
                      </p>
                    )}

                    {/* Per-item special instructions */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Special instructions for this item
                      </label>
                      <textarea
                        value={item.notes || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCart(prev =>
                            prev.map(ci =>
                              ci.id === item.id ? { ...ci, notes: value } : ci
                            )
                          );
                        }}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        placeholder="e.g. No onions, extra spicy, sauce on the side"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Pickup time selection */}
              <div
                className={`rounded-lg shadow-lg p-6 mb-6 border-2 ${
                  pickupTime
                    ? 'bg-white border-green-200'
                    : 'bg-amber-50 border-amber-400'
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h2 className="text-lg font-extrabold text-gray-900">
                    Pickup Time <span className="text-red-600">*</span>
                  </h2>
                  {!pickupTime && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-amber-500 text-amber-950 animate-pulse">
                      Required
                    </span>
                  )}
                </div>
                <p className={`text-sm mb-3 ${pickupTime ? 'text-gray-600' : 'text-amber-900 font-semibold'}`}>
                  {pickupTime
                    ? 'Pickup time selected. You can change it here if needed.'
                    : 'Please select a pickup time to continue to checkout.'}
                </p>
                <select
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  className={`w-full px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 text-sm ${
                    pickupTime
                      ? 'border border-gray-300 focus:ring-green-500 focus:border-green-500'
                      : 'border-2 border-amber-500 focus:ring-amber-500 focus:border-amber-600 bg-white'
                  }`}
                >
                  <option value="">Select pickup time…</option>
                  {generatePickupOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {!pickupTime && (
                  <div className="mt-3 p-3 rounded-md bg-white border border-amber-200 text-sm text-amber-900">
                    Checkout will stay disabled until a pickup time is selected.
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Subtotal (ex GST)</span>
                  <span className="text-gray-800 font-semibold">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">GST (15%)</span>
                  <span className="text-gray-800 font-semibold">${taxAmount.toFixed(2)}</span>
                </div>
                <div className="border-t pt-4 flex justify-between items-center">
                  <span className="text-xl font-bold text-gray-800">Total</span>
                  <span className="text-2xl font-bold text-pink-600">${totalAmount.toFixed(2)}</span>
                </div>
              </div>

              {orderingHoursRestricted && !withinOrderingHours && (
                <p className="text-amber-700 text-sm font-medium mb-2">
                  {closedHoursText}
                </p>
              )}
              <button
                onClick={handleCheckout}
                disabled={cart.length === 0 || !pickupTime || !withinOrderingHours}
                className="w-full bg-pink-500 text-white rounded-lg py-4 font-semibold text-lg hover:bg-pink-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Proceed to Checkout
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Menu view
  return (
    <div className="online-order-root min-h-screen bg-gray-50">
      {/* Header + sticky category tabs */}
      {orderingHoursRestricted && !withinOrderingHours && (
        <div className="bg-amber-500 text-amber-900 px-4 py-3 text-center font-semibold text-sm sm:text-base">
          {closedHoursText} You can browse the menu while ordering is closed.
        </div>
      )}
      <header ref={menuHeaderRef} className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          {/* Brand logo (reuses the same logo as login/standby screens) */}
          <div className="flex items-center">
            <img
              src="/logo.png"
              alt="KINGS PARK"
              className="h-8 sm:h-10 w-auto object-contain"
              onError={(e) => {
                // Fallback to text if logo image fails to load
                const target = e.currentTarget;
                const parent = target.parentElement;
                if (parent) {
                  parent.replaceChild(
                    Object.assign(document.createElement('span'), {
                      textContent: 'KINGS PARK',
                      className: 'text-xl sm:text-2xl font-bold text-gray-800',
                    }),
                    target
                  );
                }
              }}
            />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {cart.length > 0 && cartHoldSecondsLeft != null && cartHoldSecondsLeft > 0 ? (
              <div
                className={`hidden sm:flex flex-col items-end text-xs font-semibold tabular-nums leading-tight ${
                  cartHoldSecondsLeft <= 120 ? 'text-red-600' : 'text-amber-700'
                }`}
                aria-live="polite"
              >
                <span>Cart hold</span>
                <span>{formatCartHoldLabel(cartHoldSecondsLeft)}</span>
              </div>
            ) : null}
            {businessInfo?.mealPlansEnabled === true && (
              <button
                type="button"
                onClick={() => {
                  setMealPlanReturnMessage(null);
                  setView('mealPlans');
                }}
                className="relative flex items-center space-x-1 sm:space-x-2 bg-indigo-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <GiftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="font-semibold text-sm sm:text-base">Meal plan</span>
              </button>
            )}
            <button
              onClick={() => setView('cart')}
              className="relative flex items-center space-x-2 bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600 transition-colors"
            >
              <ShoppingCartIcon className="w-6 h-6" />
              <span className="font-semibold">Cart</span>
              {cart.length > 0 && cartHoldSecondsLeft != null && cartHoldSecondsLeft > 0 ? (
                <span
                  className={`sm:hidden ml-1 text-xs font-bold tabular-nums ${
                    cartHoldSecondsLeft <= 120 ? 'text-red-100' : 'text-pink-100'
                  }`}
                >
                  {formatCartHoldLabel(cartHoldSecondsLeft)}
                </span>
              ) : null}
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
        {cart.length > 0 && cartHoldSecondsLeft != null && cartHoldSecondsLeft > 0 ? (
          <div
            className={`border-t px-4 py-2 text-center text-sm font-semibold tabular-nums ${
              cartHoldSecondsLeft <= 120
                ? 'bg-red-50 text-red-700 border-red-200'
                : 'bg-amber-50 text-amber-900 border-amber-200'
            }`}
          >
            Complete checkout within{' '}
            <span className="font-bold">{formatCartHoldLabel(cartHoldSecondsLeft)}</span>
            {' '}or your cart and pickup time will be cleared.
          </div>
        ) : null}
        {/* Category Tabs (sticky under header) */}
        <div className="border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-2 flex space-x-2 overflow-x-auto scroll-smooth">
            {menuCategorySections.map(({ category }) => (
              <button
                key={category.id}
                ref={(el) => {
                  const key = String(category.id);
                  if (el) categoryTabRefs.current.set(key, el);
                  else categoryTabRefs.current.delete(key);
                }}
                onClick={() => handleCategoryClick(category.id)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                  String(selectedCategoryId) === String(category.id)
                    ? 'bg-pink-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">

        {/* Continuous menu: all categories in one scrollable page */}
        <div className="space-y-8 sm:space-y-10">
          {menuCategorySections.map(({ category, items }) => (
            <section
              key={category.id}
              ref={(el) => {
                const key = String(category.id);
                if (el) categorySectionRefs.current.set(key, el);
                else categorySectionRefs.current.delete(key);
              }}
              data-category-id={String(category.id)}
            >
              <h2
                ref={(el) => {
                  const key = String(category.id);
                  if (el) categoryHeadingRefs.current.set(key, el);
                  else categoryHeadingRefs.current.delete(key);
                }}
                data-category-id={String(category.id)}
                className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 px-1 scroll-mt-44"
                style={{ scrollMarginTop: menuStickyOffset }}
              >
                {category.name}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-6">
                {items.map((item) => renderMenuItemCard(item))}
              </div>
            </section>
          ))}
        </div>

        {menuCategorySections.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No menu items available</p>
          </div>
        )}
        {/* Item details modal for long descriptions */}
        {detailItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-30 flex items-center justify-center px-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6 relative">
              <button
                type="button"
                onClick={() => setDetailItem(null)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                ✕
              </button>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{detailItem.name}</h2>
              {detailItem.imageUrl && (
                <MenuItemImage
                  src={detailItem.imageUrl}
                  alt={detailItem.name}
                  className="w-full h-40 rounded-lg mb-3"
                  loading="eager"
                  width={400}
                  height={160}
                />
              )}
              {detailItem.description && (
                <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
                  {detailItem.description}
                </p>
              )}
              {detailItem.allergens && detailItem.allergens.length > 0 && (
                <div className="mb-3">
                  <h3 className="text-xs font-semibold text-red-700 mb-1">Allergens</h3>
                  <div className="flex flex-wrap gap-1">
                    {detailItem.allergens.map((allergen, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200"
                      >
                        ⚠️ {allergen}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {detailItem.allergyWarning && (
                <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                  <strong>⚠️ Warning:</strong> {detailItem.allergyWarning}
                </div>
              )}
              <p className="text-pink-600 font-bold text-lg mb-4">
                ${detailItem.price.toFixed(2)}
              </p>
              <button
                type="button"
                onClick={() => {
                  addToCart(detailItem);
                  setDetailItem(null);
                }}
                className="w-full bg-pink-500 text-white rounded-lg py-2 font-semibold hover:bg-pink-600 transition-colors"
              >
                Add to Cart
              </button>
            </div>
          </div>
        )}
        {/* Option selection modal for items with options */}
        {meatOptionModalItem && (
          <MeatOptionModal
            item={meatOptionModalItem}
            onSelect={handleMeatOptionSelected}
            onCancel={() => setMeatOptionModalItem(null)}
          />
        )}
      </div>
    </div>
  );
};

export default OnlineOrderPage;
